import { CheckIcon, ClockIcon, TrashIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";

import BreadcrumbsWrapper from "~/components/BreadcrumbsV2/BreadcrumbsWrapper.tsx";
import BreadcrumbElement from "~/components/BreadcrumbsV2/components/BreadcrumbElement.tsx";
import ButtonWithPopoverDots from "~/components/buttons/ButtonWithPopoverDots.tsx";
import Card from "~/components/Card/Card.tsx";
import CardValueFullWidthWithOptionalBorder
	from "~/components/Card/components/CardValueFullWidthWithOptionalBorder.tsx";
import CompanyLogo from "~/components/CompanyLogo.tsx";
import ContentWrapper from "~/components/ContentWrapper.tsx";
import Headline from "~/components/Headline/Headline.tsx";
import Hint from "~/components/Hint";
import Tooltip from "~/components/Tooltip.tsx";
import { INVOICES_INDEX_PATH } from "~/constants/appRoutes.ts";
import { EMPTY_VALUE_PLACEHOLDER } from "~/constants/textConstants.ts";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import { getPhaseOrderPath } from "~/modules/project/utils/projectDetailsViewUtils.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import QuotaBudgetStats from "~/modules/quota/components/QuotaBudgetStats.tsx";
import {
	AttachQuotaInvoicesSidebar,
} from "~/modules/quota/components/QuotaDetailsView/components/AttachQuotaInvoicesSidebar/AttachQuotaInvoicesSidebar.tsx";
import {
	DeleteQuotaSidebar,
} from "~/modules/quota/components/QuotaDetailsView/components/DeleteQuotaSidebar/DeleteQuotaSidebar.tsx";
import {
	DetachQuotaInvoiceSidebar,
} from "~/modules/quota/components/QuotaDetailsView/components/DetachQuotaInvoiceSidebar/DetachQuotaInvoiceSidebar.tsx";
import { QuotaInvoicesCard } from "~/modules/quota/components/QuotaDetailsView/components/QuotaInvoicesCard.tsx";
import QuotaStatusBadge from "~/modules/quota/components/QuotaDetailsView/components/QuotaStatusBadge.tsx";
import {
	UpdateQuotaConsumersSidebar,
} from "~/modules/quota/components/QuotaDetailsView/components/UpdateQuotaConsumersSidebar/UpdateQuotaConsumersSidebar.tsx";
import {
	UpdateQuotaDetailsSidebar,
} from "~/modules/quota/components/QuotaDetailsView/components/UpdateQuotaDetailsSidebar/UpdateQuotaDetailsSidebar.tsx";
import UpdateQuotaStatusSidebar
	from "~/modules/quota/components/QuotaDetailsView/components/UpdateQuotaStatusSidebar/UpdateQuotaStatusSidebar.tsx";
import type {
	ValidQuotaDetailsNavigatedFromOrigin,
} from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";
import {
	formatQuotaUsablePeriod,
	getQuotaDetailsBackPath,
	validQuotaDetailsNavigatedFromOrigins,
} from "~/modules/quota/components/QuotaDetailsView/quotaDetailsUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	companyData: Company;
	navigatedFrom?: ValidQuotaDetailsNavigatedFromOrigin;
	navigatedFromInvoiceId?: string;
	navigatedFromProjectId?: string;
	quotaData: QuotaWithRelations;
};

export const QuotaDetailsView = ({
	companyData,
	navigatedFrom,
	navigatedFromInvoiceId,
	navigatedFromProjectId,
	quotaData,
}: Props) => {
	const [showUpdateDetailsSidebar, setShowUpdateDetailsSidebar] = useState<boolean>(false);
	const [showDeleteQuotaSidebar, setShowDeleteQuotaSidebar] = useState<boolean>(false);
	const [showUpdateConsumersSidebar, setShowUpdateConsumersSidebar] = useState<boolean>(false);
	const [showUpdateStatusSidebar, setShowUpdateStatusSidebar] = useState<boolean>(false);
	const [showAttachInvoicesSidebar, setShowAttachInvoicesSidebar] = useState<boolean>(false);
	const [invoiceToDetach, setInvoiceToDetach] = useState<Invoice | null>(null);

	// An invoice-backed quota with no attached invoice is a shell. It is not usable and takes no consumers.
	const isInvoiceBacked = quotaData.type === "invoice-backed";
	const isShell = isInvoiceBacked && quotaData.invoices.length === 0;

	const Breadcrumbs = useMemo(() => {
		// The invoice crumb names the invoice of the origin link. An old link without it uses the first invoice.
		const originInvoice = quotaData.invoices.find((invoice) => invoice.id === navigatedFromInvoiceId)
			?? quotaData.invoices[0];
		const path = getQuotaDetailsBackPath(navigatedFrom, {
			invoiceId: originInvoice?.id,
			projectId: navigatedFromProjectId,
		});

		if (navigatedFrom === validQuotaDetailsNavigatedFromOrigins.PROJECT && navigatedFromProjectId) {
			// The project title comes from the order embed of a consumer in that project (BE1).
			const projectTitle = quotaData.consumers
				.find((consumer) => consumer.order?.projectId === navigatedFromProjectId)?.order?.project.title;
			return <BreadcrumbElement text={projectTitle ?? "Projekt"}
									  url={path} />;
		} else if (navigatedFrom === validQuotaDetailsNavigatedFromOrigins.INVOICE && originInvoice) {
			return <>
				<BreadcrumbElement text="Rechnungen"
								   url={INVOICES_INDEX_PATH} />
				<BreadcrumbElement text={originInvoice.invoiceNumber ?? "Rechnung"}
								   url={path} />
			</>;
		} else if (navigatedFrom === validQuotaDetailsNavigatedFromOrigins.PENDING_INVOICES) {
			return <BreadcrumbElement text="Offene Rechnungen"
									  url={path} />;
		} else {
			return <BreadcrumbElement text="Abrechnungskontingente"
									  url={path} />;
		}
	}, [navigatedFrom, navigatedFromInvoiceId, navigatedFromProjectId, quotaData.consumers, quotaData.invoices]);

	return <div className="auto-grid-2-rows grid h-screen">
		<div className="flex w-full flex-col bg-white">
			<ContentWrapper className="w-full pb-4">
				<BreadcrumbsWrapper>
					{Breadcrumbs}
					<BreadcrumbElement text={quotaData.title} />
				</BreadcrumbsWrapper>
				<div className="flex items-center justify-between gap-x-4 pt-2">
					<div className="flex min-w-0 items-center gap-x-3">
						{companyData.avatarImage && <div className="shrink-0">
							<CompanyLogo image={companyData.avatarImage}
										 sizeClassName="size-10" />
						</div>}
						<Headline type="h2"
								  className="truncate">{quotaData.title}</Headline>
					</div>
					<div className="flex shrink-0 items-center gap-x-2">
						<QuotaStatusBadge isBookable={quotaData.isBookable} />
						<ButtonWithPopoverDots items={[
							{
								label: "Status ändern",
								onClick: () => setShowUpdateStatusSidebar(true),
								icon: CheckIcon,
							},
							{
								label: "Kontingent löschen",
								onClick: () => setShowDeleteQuotaSidebar(true),
								icon: TrashIcon,
							},
						]}
											   theme="dark" />
					</div>
				</div>
			</ContentWrapper>
		</div>
		<ContentWrapper className="flex size-full flex-col gap-y-4 overflow-y-scroll pb-10 pt-4">
			<Card title="Budget">
				{isShell || quotaData.amountCents === null ?
					<Hint title="Nicht nutzbar">
						<p>Das Kontingent ist erst nutzbar, wenn ihm eine Rechnung zugewiesen ist.</p>
						{quotaData.amountCents !== null &&
							<p>Platzhalter-Budget: {formatCentsToCurrency(quotaData.amountCents)}</p>}
					</Hint> :
					<QuotaBudgetStats amountCents={quotaData.amountCents}
									  amountLabel={isInvoiceBacked ? "Budget (Überschuss)" : undefined}
									  assignedCents={quotaData.assignedPotCents}
									  budgetCentsConsumed={quotaData.budgetCentsConsumed} />}
			</Card>
			<Card title="Kontingentdetails"
				  headerButtonText="Bearbeiten"
				  onHeaderButtonClick={() => setShowUpdateDetailsSidebar(true)}
				  padding="none">
				<CardValueFullWidthWithOptionalBorder label="Titel / Beschreibung">
					{quotaData.title}
				</CardValueFullWidthWithOptionalBorder>
				<div className="grid grid-cols-4">
					<div className="col-span-2">
						<CardValueFullWidthWithOptionalBorder label="Kunde">
							{companyData.displayName}
						</CardValueFullWidthWithOptionalBorder>
					</div>
					<div className="col-span-2">
						<CardValueFullWidthWithOptionalBorder label="Kontaktperson Kunde">
							{quotaData.clientContactPerson || EMPTY_VALUE_PLACEHOLDER}
						</CardValueFullWidthWithOptionalBorder>
					</div>
					<div className="col-span-2">
						<CardValueFullWidthWithOptionalBorder label="Verantwortlich">
							{quotaData.manager?.fullName ?? EMPTY_VALUE_PLACEHOLDER}
						</CardValueFullWidthWithOptionalBorder>
					</div>
					<CardValueFullWidthWithOptionalBorder label="Nutzbar im Zeitraum">
						{formatQuotaUsablePeriod(quotaData.usableFrom, quotaData.usableUntil)}
					</CardValueFullWidthWithOptionalBorder>
					<CardValueFullWidthWithOptionalBorder label="Vorausgewählt im Erfassungsformular">
						{quotaData.preselectForTimeTracking ? "Ja" : "Nein"}
					</CardValueFullWidthWithOptionalBorder>
				</div>
				<CardValueFullWidthWithOptionalBorder label="Anmerkungen"
													  hasBottomBorder={false}>
					<span className="whitespace-pre-line">{quotaData.comment || EMPTY_VALUE_PLACEHOLDER}</span>
				</CardValueFullWidthWithOptionalBorder>
			</Card>
			<QuotaInvoicesCard onAttachClick={() => setShowAttachInvoicesSidebar(true)}
							   onDetachClick={setInvoiceToDetach}
							   quota={quotaData} />
			<Card title="Enthaltene Bestellungen"
				  headerButtonText={isShell ? undefined : "Bearbeiten"}
				  onHeaderButtonClick={isShell ? undefined : () => setShowUpdateConsumersSidebar(true)}>
				<div className="mt-2 space-y-4">
					{/* The consumers sync refuses a non-empty set on a shell. */}
					{isShell &&
						<div className="text-sm text-gray-500">Erst eine Rechnung zuweisen, dann Bestellungen.</div>}
					{!isShell && quotaData.consumers.length === 0 &&
						<div className="text-sm text-gray-500">Keine Bestellungen zugewiesen.</div>}
					{quotaData.consumers.map((consumer) => {
						// The quota response with relations embeds the order of each consumer (BE1).
						const order = consumer.order;
						if (!order) return null;
						return <NavLink key={consumer.id}
										to={getPhaseOrderPath(order.projectId, order.projectPhase.phaseNumber, order.id, "quotas")}
										className="group flex grow items-center justify-between rounded-lg border border-gray-200  px-4 hover:bg-gray-100">
							<div>
								<Headline type="h5"
										  color="muted">{order.title}</Headline>
								<div className="text-sm text-gray-500">{`${order.project.titleWithNumber} · ${order.projectPhase.title}`}</div>
							</div>
							<div className="ml-auto" />
							<div>
								<CardValueFullWidthWithOptionalBorder label="Zugewiesen"
																	  hasBottomBorder={false}><span className="font-bold text-gray-700">{consumer.budgetCentsAssigned !== null ? formatCentsToCurrency(consumer.budgetCentsAssigned) : EMPTY_VALUE_PLACEHOLDER}</span></CardValueFullWidthWithOptionalBorder>
							</div>
							<div>
								<CardValueFullWidthWithOptionalBorder label="Verfügt"
																	  hasBottomBorder={false}><span className="font-bold text-gray-700">{formatCentsToCurrency(consumer.budgetCentsConsumed)}</span></CardValueFullWidthWithOptionalBorder>
							</div>
							<Tooltip text={consumer.timeTrackingEnabled ? "Zeiterfassung aktiv" : "Zeiterfassung inaktiv"}>
									<span>
										<ClockIcon className={clsx("size-6", consumer.timeTrackingEnabled
											? "text-success-500"
											: "text-danger-500")} />
									</span>
							</Tooltip>
						</NavLink>;
					})}
				</div>
			</Card>
		</ContentWrapper>
		<UpdateQuotaDetailsSidebar isOpen={showUpdateDetailsSidebar}
								   onClose={() => setShowUpdateDetailsSidebar(false)}
								   quota={quotaData} />

		<DeleteQuotaSidebar isOpen={showDeleteQuotaSidebar}
							onClose={() => setShowDeleteQuotaSidebar(false)}
							quota={quotaData} />

		<UpdateQuotaConsumersSidebar isOpen={showUpdateConsumersSidebar}
									 onClose={() => setShowUpdateConsumersSidebar(false)}
									 quota={quotaData} />

		<UpdateQuotaStatusSidebar isOpen={showUpdateStatusSidebar}
								  onClose={() => setShowUpdateStatusSidebar(false)}
								  quota={quotaData} />

		<AttachQuotaInvoicesSidebar isOpen={showAttachInvoicesSidebar}
									onClose={() => setShowAttachInvoicesSidebar(false)}
									quota={quotaData} />

		<DetachQuotaInvoiceSidebar invoice={invoiceToDetach}
								   onClose={() => setInvoiceToDetach(null)}
								   quota={quotaData} />

	</div>;
};